/* eslint-disable react-hooks/exhaustive-deps */
import { useQueryClient } from "@tanstack/react-query";
import { SocketsContext } from "libs/soket";
import { useContext, useEffect } from "react";

import {
  ESupportTicketSource,
  SupportTicketResponse,
} from "../aiTickets.types";
import { SUPPORT_TICKETS_QUERY_KEY } from "../constants/enums";

const conversationIdOf = (ticket: SupportTicketResponse): string | null =>
  ticket.source === ESupportTicketSource.WhatsApp
    ? ticket.whatsappConversationId
    : ticket.inAppConversationId;

// Live-updates the open ticket's chat. WhatsApp and in-app support are separate
// gateways with separate namespaces and event names, so we pick both the socket
// and the event pair by source, then refetch this conversation's transcript /
// conversation query whenever an event targets it. (The list-level
// useSupportTicketsRealTime hook only watches the tickets list, not the chat.)
export const useTicketChatRealTime = (ticket: SupportTicketResponse | null) => {
  const { whatsappBotAdminSocket, inAppSupportAdminSocket } =
    useContext(SocketsContext);
  const queryClient = useQueryClient();

  const source = ticket?.source ?? null;
  const conversationId = ticket ? conversationIdOf(ticket) : null;

  useEffect(() => {
    if (!source || !conversationId) return;

    const isWhatsApp = source === ESupportTicketSource.WhatsApp;
    const socket = (
      isWhatsApp ? whatsappBotAdminSocket : inAppSupportAdminSocket
    ).socket;
    if (!socket) return;

    const invalidate = (key: "transcript" | "conversation") =>
      queryClient.invalidateQueries({
        queryKey: [SUPPORT_TICKETS_QUERY_KEY, key, source, conversationId],
      });

    const onMessage = (msg: { conversationId?: string }) => {
      if (msg?.conversationId === conversationId) invalidate("transcript");
    };
    const onConversationUpdated = (conv: { id?: string }) => {
      if (conv?.id === conversationId) invalidate("conversation");
    };

    const messageEvent = isWhatsApp
      ? "message.created"
      : "in-app-message.created";
    const conversationEvent = isWhatsApp
      ? "conversation.updated"
      : "in-app-conversation.updated";

    socket.on(messageEvent, onMessage);
    socket.on(conversationEvent, onConversationUpdated);

    return () => {
      socket.off(messageEvent, onMessage);
      socket.off(conversationEvent, onConversationUpdated);
    };
  }, [
    source,
    conversationId,
    whatsappBotAdminSocket.socket,
    inAppSupportAdminSocket.socket,
  ]);
};
